import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useAuthRequestMutationWrapper from 'Src/feature/auth-request/useAuthRequestMutationWrapper';
import { ServiceError, useError } from 'Src/network/useError';

import service, { PayInOutGetAllReturn, PayInOutReason } from './payInOutService';

const queryKeys = {
  getAll: () => ['POS:GET:Payment.PayInOuts'],
  getAllReasons: () => ['POS:GET:PayInOutReasons'],
};

const useQuery_PayInOutGetAll = <T = PayInOutGetAllReturn>(select?: (data?: PayInOutGetAllReturn) => T) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('payInOut.errors.getAll') });
  return useQuery<PayInOutGetAllReturn, ServiceError, T>({
    queryKey: queryKeys.getAll(),
    queryFn: async () => {
      try {
        return await service.getAll({});
      } catch (e) {
        throw parseError(e);
      }
    },
    select,
  });
};

const useQuery_PayInOutGetAllReasons = <T = PayInOutReason[]>(select?: (data?: PayInOutReason[]) => T) => {
  const { t } = useTranslation();
  const { parseError } = useError({ activity: t('payInOut.errors.getAllReasons') });
  return useQuery<PayInOutReason[], ServiceError, T>({
    queryKey: queryKeys.getAllReasons(),
    queryFn: async () => {
      try {
        return await service.getAllReasons({});
      } catch (e) {
        throw parseError(e);
      }
    },
    select,
  });
};

const useQuery_payInOutReasonMap = () => useQuery_PayInOutGetAllReasons((data) => {
  const map = new Map<number, PayInOutReason>();
  if (data && data.length) data.forEach((item) => map.set(item.id, item));
  return map;
});
const useQuery_PayInReasonOptions = () =>
  useQuery_PayInOutGetAllReasons((data) => data && data.length ? data.filter(({ isPayIn }) => isPayIn).map(({ id, name }) => ({ value: id, label: name })) : []);
const useQuery_PayOutReasonOptions = () =>
  useQuery_PayInOutGetAllReasons((data) => data && data.length ? data.filter(({ isPayIn }) => !isPayIn).map(({ id, name }) => ({ value: id, label: name })) : []);

const useMutation_PayIn = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { parseError } = useError({ activity: t('payInOut.errors.payIn') });
  const { authWrapper } = useAuthRequestMutationWrapper(service.payIn);
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.payIn>) => {
      try {
        return await authWrapper(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.getAll() });
    },
    onError,
  });
};

const useMutation_PayOut = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { parseError } = useError({ activity: t('payInOut.errors.payOut') });
  const { authWrapper } = useAuthRequestMutationWrapper(service.payOut);
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.payOut>) => {
      try {
        return await authWrapper(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.getAll() });
    },
    onError,
  });
};

const useMutation_VoidPayInOut = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { parseError } = useError({ activity: t('payInOut.errors.void') });
  const { authWrapper } = useAuthRequestMutationWrapper(service.void);
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.void>) => {
      try {
        return await authWrapper(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.getAll() });
    },
    onError,
  });
};

const useMutation_PayInManual = (onError?: (error: ServiceError) => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { parseError } = useError({ activity: t('payInOut.errors.payInManual') });
  const { authWrapper } = useAuthRequestMutationWrapper(service.payInManual);
  return useMutation({
    mutationFn: async (...args: Parameters<typeof service.payInManual>) => {
      try {
        return await authWrapper(...args);
      } catch (e) {
        throw parseError(e);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.getAll() });
    },
    onError,
  });
};

const usePayInOut = {
  query: {
    getAll: useQuery_PayInOutGetAll,
    getAllReasons: useQuery_PayInOutGetAllReasons,
    reason: {
      map: useQuery_payInOutReasonMap,
      options: {
        payIn: useQuery_PayInReasonOptions,
        payOut: useQuery_PayOutReasonOptions,
      },
    },
  },
  mutate: {
    payIn: useMutation_PayIn,
    payInManual: useMutation_PayInManual,
    payOut: useMutation_PayOut,
    void: useMutation_VoidPayInOut,
  },
};

export { usePayInOut };
